import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '../../core/auth.service';
import { PageMetaService } from '../../../reminders/core/services/page-meta.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './forgot-password.html',
  styleUrl: '../auth-page.scss',
})
export class ForgotPassword implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly pageMeta = inject(PageMetaService);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly currentYear = new Date().getFullYear();

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit(): void {
    this.pageMeta.set(
      'Forgot password - Every Rupee Spent',
      'Reset your Every Rupee Spent password by email.',
    );
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    const { email } = this.form.getRawValue();

    this.authService.forgotPassword(email).subscribe({
      // The backend always responds the same way whether or not the email is registered,
      // so a request that reaches the server is treated as a success here too.
      next: () => this.onRequestSent(email),
      error: (err: unknown) => {
        this.loading.set(false);
        if (err instanceof HttpErrorResponse && err.status === 0) {
          this.errorMessage.set('Could not reach the server. Check your connection and try again.');
          return;
        }
        if (err instanceof HttpErrorResponse && err.status === 429) {
          this.errorMessage.set('Too many attempts. Please wait a minute and try again.');
          return;
        }
        // Any other server-side failure - still show the generic success state so this
        // endpoint never reveals whether an email address is registered.
        this.onRequestSent(email);
      },
    });
  }

  private onRequestSent(email: string): void {
    this.loading.set(false);
    this.router.navigate(['/reset-password'], { queryParams: { email } });
  }
}
